import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackIconButton } from '@/components/topbar/TopBarBackIconButton'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ListSkeleton } from '@/components/ui/skeleton-presets'
import { useCrewGetAll } from '@/lib/hooks'
import { ArrowLeft } from '@/components/icons'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'

export const Route = createFileRoute('/_auth/operations/settings/owneradmins')({
  component: OwnerAdminsRoute,
})


function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.[0]?.toUpperCase() ?? ''
  const last = lastName?.[0]?.toUpperCase() ?? ''
  return `${first}${last}` || '?'
}

function OwnerAdminsRoute() {
  const { data: crew, isLoading } = useCrewGetAll()

  const ownersAndAdmins = crew?.filter((c) => c.isOwner || c.isAdmin) ?? []

  const isDesktop = useIsDesktop()
  const navigate = useNavigate()

  const body = (
    <>
      {isLoading ? (
        <ListSkeleton twoLine={false} />
      ) : (
        <Card className="w-full gap-0 ">
          {ownersAndAdmins.map((member, index) => {
            const initials = getInitials(member.firstName, member.lastName)
            const color = member.avatarColor
            const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || '—'

            return (
              <div key={member.id}>
                {index > 0 && <div className="h-px bg-border" />}
                <div className="flex items-center justify-between py-3 px-1">
                  <div className="flex items-center gap-3">
                    <div
                      className="size-6 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      <span className="text-[10px] font-semibold text-white leading-none">
                        {initials}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
                      {name}
                    </span>
                  </div>
                  <Badge className="rounded-full text-xs font-semibold px-2 py-0.5">
                    {member.isOwner ? 'Owner' : 'Admin'}
                  </Badge>
                </div>
              </div>
            )
          })}

          {ownersAndAdmins.length === 0 && (
            <p className="text-sm text-content-primary-subtle text-center py-4">No owners or admins found.</p>
          )}
        </Card>
      )}
    </>
  )

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/operations/settings' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to settings"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/operations" className="hover:underline">
          Operations
        </Link>
        <span className="text-content-primary-subtle"> / </span>
        <Link to="/operations/settings" className="hover:underline">
          Settings
        </Link>
        <span className="text-content-primary-subtle"> / Owners &amp; Admins</span>
      </span>
    </span>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Owners & Admins" breadcrumb={breadcrumb} />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={<TopBarBackIconButton title="Owners & Admins" />}
      className="pt-6"
    >
      {body}
    </AppLayout>
  )
}
